import React from 'react';
import styled from 'styled-components';
import { FlexColumn, Text } from '@make-software/cspr-design';
import { useTranslation } from 'react-i18next';
import { Button } from '@block-explorer/ui';
import { PageLayout } from '../../layouts';
import { Breadcrumb, LegacyPageBanner } from '../../components';
import { isValidAccountHash, isValidPublicKey } from '../../utils/account';
import { NothingFoundScene } from '../NothingFoundScene';
import { ACCOUNT_DETAILS_PATH } from '../../router/paths';
import { buildRouterPath, useRouterPath } from '../../router/utils';
import AccountDetails from './components/AccountDetails';
import AccountTabs from './components/AccountTabs';
import { useGetAccountInfo } from '../../hooks/event-store-hooks';
import { AccountModel } from '@block-explorer/entities';
import { DetailsAdsBanner } from '../../components/ads-banner/ads-banners-by-page';

export const StyledButton = styled(Button)(({ theme }) => ({
  width: 177,
  height: [50, 50, 36, 36],
  padding: '8px 16px',
}));

export interface AccountDetailsSceneProps {}

export const AccountDetailsScene = (props: AccountDetailsSceneProps) => {
  // view should only be accessed as /account/:id.
  // - when search - redirects here
  // - when View Address - redirects here
  // - when you sign into Signer, and you click on account dropdown - redirects here
  const { t } = useTranslation();
  const {
    urlParams: { id },
  } = useRouterPath(ACCOUNT_DETAILS_PATH);

  const lowerCaseId = id?.toLowerCase();

  if (!isValidPublicKey(lowerCaseId) && !isValidAccountHash(lowerCaseId)) {
    return <NothingFoundScene />;
  }

  const accountHash = isValidPublicKey(lowerCaseId)
    ? AccountModel({
        publicKeyHex: lowerCaseId,
      })
        .getAccountHash()
        .toLowerCase()
    : lowerCaseId;

  const accountDataResponse = useGetAccountInfo(
    accountHash
      ? {
          accountHash,
          includes:
            'delegated_balance,staked_balance,undelegating_balance,auction_status,centralized_account_info,account_info,cspr_name',
        }
      : null
  );

  const accountHashSpecialSuffix = isValidAccountHash(lowerCaseId)
    ? ' (account hash)'
    : '';

  const csprName = accountDataResponse.data?.data.cspr_name;
  const publicKey = accountDataResponse.data?.data.public_key || lowerCaseId;

  return (
    <PageLayout
      title="Account (Legacy)"
      renderHeader={() => (
        <FlexColumn itemsSpacing={24}>
          <LegacyPageBanner
            newVersionLink={buildRouterPath(
              ACCOUNT_DETAILS_PATH,
              { id },
              undefined
            )}
            title={t('Account')}
          />
          <Breadcrumb suffix={accountHashSpecialSuffix}>
            {csprName ? <Text>{csprName}</Text> : null}
          </Breadcrumb>
        </FlexColumn>
      )}
    >
      <FlexColumn>
        <AccountDetails
          publicKey={publicKey}
          accountHash={accountHash}
          {...accountDataResponse}
        />
        <DetailsAdsBanner />
        <AccountTabs publicKey={publicKey} accountHash={accountHash} />
      </FlexColumn>
    </PageLayout>
  );
};

export default AccountDetailsScene;
